import {ChangeDetectionStrategy, Component, inject, input, OnInit, output} from '@angular/core';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from "@angular/material/expansion";
import {
  advertisementStatisticsAccordions,
  advertisementStatisticsFormFields
} from "../../configs/advertisement-statistics-modal-config";
import {MatFormField, MatInput, MatLabel} from "@angular/material/input";
import {
  CustomMatSelectComponent
} from "../../../../../../shared/components/ui/custom-mat-select/custom-mat-select.component";
import {CdkTextareaAutosize} from "@angular/cdk/text-field";
import {MatButtonModule} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {HistoryChangesData} from "../../../history-changes-modal/types/history-changes-modal.types";
import {ModalsService} from "../../../../../services/modals.service";
import {AdvertisementStatisticsFormField} from "../../types/advertisement-statistics-modal.types";
import {GetSelectDataParams, RecordData, SelectData} from "../../../../../types/records-table.types";
import {
  contentForDistributionTypePropertyLazy,
  contentForVideoTypePropertyLazy,
  videoScoreSelectData
} from "../../configs/advertisement-statistics-select-data";
import {EditorTextModalComponent} from "../../../editor-text-modal/editor-text-modal.component";
import {ConfirmationStatus} from "../../../../../types/confirmation-modal.types";
import {FormGroup, ReactiveFormsModule} from "@angular/forms";
import {AdvertisementStatisticsModalService} from "../../services/advertisement-statistics-modal.service";
import {BaseModalService} from "../../../services/base-modal.service";
import {MatDialog} from "@angular/material/dialog";
import {FormFieldUpdate} from "../../../../../types/file.types";
import {IconStatusDirective} from "../../../../../../shared/directives/icon-status.directive";
import {IconStatus} from "../../../../../models/icon-status-config";

@Component({
  selector: 'app-advertisement-statistics-fields',
  imports: [
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatFormField,
    MatLabel,
    MatInput,
    CustomMatSelectComponent,
    CdkTextareaAutosize,
    MatButtonModule,
    MatIcon,
    ReactiveFormsModule,
    IconStatusDirective
  ],
  templateUrl: './advertisement-statistics-fields.component.html',
  styleUrls: [
    './advertisement-statistics-fields.component.scss',
    '../../../base-modal/base-modal.component.scss'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdvertisementStatisticsFieldsComponent implements OnInit {
  isEditModeActive = input<boolean>(false);
  recordData = input.required<RecordData>();
  updatedField = output<FormFieldUpdate>();

  private modalsService: ModalsService = inject(ModalsService);
  private advertisementStatisticsModalService: AdvertisementStatisticsModalService = inject(AdvertisementStatisticsModalService);
  private baseModalService: BaseModalService = inject(BaseModalService);
  private dialog: MatDialog = inject(MatDialog);

  public readonly advertisementStatisticsAccordions = advertisementStatisticsAccordions;
  public advertisementFormGroup: FormGroup = new FormGroup({});
  public contentForDistributionTypeSelectData: Array<SelectData> = [];
  public contentForVideoTypeSelectData: Array<SelectData> = [];

  public async ngOnInit(): Promise<void> {
    this.advertisementFormGroup = this.baseModalService.getFormGroup<AdvertisementStatisticsFormField>(advertisementStatisticsFormFields);
    await this.fetchContentForDistributionTypeSelectData();
    await this.fetchContentForVideoTypeSelectData();
    this.advertisementFormGroup.patchValue(this.recordData());
  }

  public getIconStatus(statusName?: string): IconStatus {
    if (!statusName) { return 'ERROR'; }

    return this.baseModalService.fetchIconStatus(this.recordData()[statusName] as IconStatus);
  }

  public getFieldClasses(): string {
    return !this.isEditModeActive() ? 'none-pointer-events' : '';
  }

  public getAccordionFields(accordionName: string): Array<AdvertisementStatisticsFormField> {
    return advertisementStatisticsFormFields.filter(i => i.accordionName === accordionName);
  }

  public async handleUpdateField(key: string): Promise<void> {
    const newValue = this.advertisementFormGroup.get(key)?.value;
    this.updatedField.emit({
      newValue: newValue,
      key: key
    });
  }

  public getOptionsByFieldName(fieldName: string): Array<SelectData> {
    const optionsMap: Record<string, Array<SelectData>> = {
      videoScore: videoScoreSelectData,
      videoTypeId: this.contentForVideoTypeSelectData,
      contentForDistributionType: this.contentForDistributionTypeSelectData
    };

    return optionsMap[fieldName] ?? [];
  }

  public getSelectedValue(fieldName: string): string {
    return String(this.advertisementFormGroup.get(fieldName)?.value) || '';
  }

  public async handleSelectedChange(value: string, key: string) {
    this.updatedField.emit({
      newValue: value,
      key: key
    });
    this.advertisementFormGroup.get(key)?.setValue(value);
  }

  public handleOpenHistoryModal(): void {
    this.modalsService.openHistoryChangesModal({
      historyChangesData: this.recordData()['history'] as Array<HistoryChangesData> || [],
      columnKey: 'history'
    });
  }

  public async handleOpenFullscreenText(key: string): Promise<void> {
    const data = {
      id: this.recordData().id,
      ...this.advertisementFormGroup.value
    };

    const textDialogData = this.advertisementStatisticsModalService.setAdvertisementStatisticsData(data, key);
    this.dialog.open(EditorTextModalComponent, {
      width: '800px',
      data: textDialogData
    }).afterClosed().subscribe((result?: string | number | ConfirmationStatus) => {
      if (result !== undefined && result !== 'cancel') {
        this.advertisementFormGroup.get(key)?.setValue(result);
      }
    });
  }

  public setFieldStyles(fieldName: string): Record<string, string> {
    return this.baseModalService.setFieldStyles({
      formControlName: fieldName,
      formGroup: this.advertisementFormGroup,
      isEditModeActive: this.isEditModeActive()
    });
  }

  public handleValueClick(fieldName: string): void {
    return this.baseModalService.handleValueClick({
      formControlName: fieldName,
      formGroup: this.advertisementFormGroup,
      isEditModeActive: this.isEditModeActive()
    });
  }

  private async fetchContentForDistributionTypeSelectData(): Promise<void> {
    const getSelectDataParams: GetSelectDataParams = {
      propertyLazySelect: contentForDistributionTypePropertyLazy,
    };

    this.contentForDistributionTypeSelectData = await this.advertisementStatisticsModalService.fetchSelectData(getSelectDataParams)
      .catch(() => []);
  }

  private async fetchContentForVideoTypeSelectData(): Promise<void> {
    const getSelectDataParams: GetSelectDataParams = {
      propertyLazySelect: contentForVideoTypePropertyLazy,
    };

    this.contentForVideoTypeSelectData = await this.advertisementStatisticsModalService.fetchSelectData(getSelectDataParams)
      .catch(() => []);
  }
}
